import React, { useEffect, useState, Children } from 'react';
import { XIcon, CheckIcon, ChevronRightIcon, ChevronLeftIcon, SparklesIcon } from 'lucide-react';
interface LoyaltyOnboardingBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: any) => void;
  initialData?: any;
}
export function LoyaltyOnboardingBottomSheet({
  isOpen,
  onClose,
  onComplete,
  initialData = {}
}: LoyaltyOnboardingBottomSheetProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    gender: initialData.gender || '',
    maritalStatus: initialData.maritalStatus || '',
    nationality: initialData.nationality || 'Saudi Arabia',
    dateOfBirth: initialData.dateOfBirth || '',
    workingSector: initialData.workingSector || '',
    city: initialData.city || '',
    district: initialData.district || '',
    preferredLanguage: initialData.preferredLanguage || 'English',
    // Optional fields
    hasChildren: initialData.hasChildren || '',
    childrenAges: initialData.childrenAges || [],
    chronicConditions: initialData.chronicConditions || [],
    interests: initialData.interests || [],
    smsConsent: initialData.smsConsent || false,
    whatsappConsent: initialData.whatsappConsent || false,
    emailConsent: initialData.emailConsent || false,
    pushConsent: initialData.pushConsent || false
  });
  // Districts by city
  const districtsByCity: {
    [key: string]: string[];
  } = {
    Riyadh: ['Al Olaya', 'Al Malaz', 'Al Naseem', 'Al Sahafa', 'Al Wurud', 'King Fahd', 'Al Hamra', 'Al Murabba', 'Al Sulaymaniyah', 'Al Izdihar'],
    Jeddah: ['Al Hamra', 'Al Rawdah', 'Al Salamah', 'Al Zahra', 'Al Andalus', 'Al Basateen', 'Al Faisaliyah', 'Al Naeem', 'Al Shatie', 'Al Khalidiyah'],
    Dammam: ['Al Faisaliyah', 'Al Shati', 'Al Noor', 'Al Adamah', 'Al Jalawiyah', 'Al Mazruiyah', 'Al Badiyah', 'Al Muhammadiyah', 'Al Anoud', 'Al Rawdah'],
    Mecca: ['Ajyad', 'Al Aziziyah', 'Al Hindawiyah', 'Al Kakiyah', 'Al Shubaikah', 'Jarwal', 'Misfalah', 'Al Rusaifah', 'Al Zahir', 'Al Utaibiyah'],
    Medina: ['Al Khalidiyah', 'Al Aziziyah', 'Al Iskan', 'Quba', 'Al Uyun', 'Al Rawdah', 'Al Faisaliyah', 'Al Jumuah', 'Al Dar', 'Al Haram'],
    Khobar: ['Al Corniche', 'Al Aqrabiyah', 'Al Hizam Al Akhdar', 'Al Thuqbah', 'Al Rakah Al Janubiyah', 'Al Ulaya', 'Al Jisr', 'Al Bandariyah', 'Al Khobar Al Shamaliyah', 'Al Yarmouk'],
    Dhahran: ['Al Doha', 'Al Faisaliyah', 'Al Jawhara', 'Al Muhammadiyah', 'Al Qusour', 'Al Thuqbah', 'Al Amamrah', 'Al Badiyah', 'Al Dana', 'Al Hamra']
  };
  const steps = [{
    id: 'basic',
    title: 'Basic Information',
    required: true
  }, {
    id: 'location',
    title: 'Location',
    required: true
  }, {
    id: 'preferences',
    title: 'Preferences',
    required: false
  }, {
    id: 'communication',
    title: 'Communication',
    required: false
  }];
  const calculateProgress = () => {
    const requiredFields = [formData.gender, formData.maritalStatus, formData.nationality, formData.dateOfBirth, formData.workingSector, formData.city, formData.district, formData.preferredLanguage];
    const completedRequired = requiredFields.filter(field => field !== '').length;
    const totalRequired = requiredFields.length;
    return Math.round(completedRequired / totalRequired * 100);
  };
  const progress = calculateProgress();
  const isStepValid = () => {
    if (currentStep === 0) {
      return formData.gender && formData.maritalStatus && formData.nationality && formData.dateOfBirth && formData.workingSector;
    }
    if (currentStep === 1) {
      return formData.city && formData.district && formData.preferredLanguage;
    }
    return true;
  };
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(formData);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleInputChange = (field: string, value: string) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    // Reset district when city changes
    if (field === 'city') {
      newFormData.district = '';
    }
    setFormData(newFormData);
  };
  useEffect(() => {
    // Save progress to localStorage
    if (isOpen) {
      localStorage.setItem('loyaltyOnboarding', JSON.stringify(formData));
    }
  }, [formData, isOpen]);
  if (!isOpen) return null;
  const currentDistricts = formData.city ? districtsByCity[formData.city] || [] : [];
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300" onClick={onClose} style={{
      backdropFilter: 'blur(4px)'
    }} data-id="element-3437" data-mp="1xeolp44m2hil" />
      {/* Bottom Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out" style={{
      maxHeight: '90vh',
      height: 'auto',
      maxWidth: '640px',
      margin: '0 auto',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)'
    }} data-id="element-3438" data-mp="2q4f0lfe2hnfr">
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2" data-id="element-3439" data-mp="14opqeqztpo5h">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-3440" data-mp="14s3q1rh3rj98" />
        </div>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-3xl z-10" data-id="element-3441" data-mp="pmpxv25ao2bu">
          <div className="flex items-center justify-between mb-3" data-id="element-3442" data-mp="ll4cesep2fq1">
            <h2 className="text-lg font-bold text-gray-900 flex items-center" data-id="element-3443" data-mp="llxmo3cv3ncx">
              <SparklesIcon className="w-5 h-5 text-[#FFD700] mr-2" data-id="element-3444" data-mp="gt4ibglb2h1d" />
              Complete Your Profile
            </h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3445" data-mp="bv2rualjvffv">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-3446" data-mp="1wn7qrkxx6fv" />
            </button>
          </div>
          {/* Progress Bar */}
          <div className="mb-2" data-id="element-3447" data-mp="3u1z91q4wf3vo">
            <div className="flex justify-between items-center mb-2" data-id="element-3448" data-mp="3u31xlrer0u8f">
              <span className="text-sm font-medium text-gray-600" data-id="element-3449" data-mp="3p361sfg7g93y">
                {steps[currentStep].title}
              </span>
              <span className="text-sm font-bold text-[#1E2B55]" data-id="element-3450" data-mp="1s7i0q5xai4a0">
                {progress}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-3451" data-mp="1uuir2aoh9pmm">
              <div className="h-full bg-gradient-to-r from-[#1E2B55] to-[#FFD700] transition-all duration-500" style={{
              width: `${progress}%`
            }} data-id="element-3452" data-mp="1zt3qw8zti7aj" />
            </div>
          </div>
          {/* Step Indicators */}
          <div className="flex justify-between mt-3" data-id="element-3453" data-mp="29kq3a8nw6cev">
            {steps.map((step, index) => <div key={step.id} className={`flex-1 h-1 rounded-full mx-0.5 ${index <= currentStep ? 'bg-[#1E2B55]' : 'bg-gray-200'}`} data-id="element-3454" data-mp="1bxhgmac5mxba" data-mpi={index} />)}
          </div>
        </div>
        {/* Content */}
        <div className="overflow-y-auto px-6 py-6" style={{
        maxHeight: 'calc(90vh - 280px)'
      }} data-id="element-3455" data-mp="1pvz6adlmejvu">
          {/* Step 0: Basic Information */}
          {currentStep === 0 && <div className="space-y-4" data-id="element-3456" data-mp="3189qvwq7n9p7">
              {/* Gender */}
              <div data-id="element-3457" data-mp="3ncejohexxqlq">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3458" data-mp="3nbwf8xi4k62q">
                  Gender <span className="text-red-500" data-id="element-3459" data-mp="3ie3924gtnc2g">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-3460" data-mp="x3k83tzvw38">
                  {['Male', 'Female', 'Prefer not to say'].map(option => <button key={option} onClick={() => handleInputChange('gender', option)} className={`py-3 px-4 rounded-lg font-medium transition-all ${formData.gender === option ? 'bg-[#1E2B55] text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3461" data-mp="3s8mtrgea6tm1">
                      {option}
                    </button>)}
                </div>
              </div>
              {/* Marital Status */}
              <div data-id="element-3462" data-mp="2lo3zc6oxq7ia">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3463" data-mp="2gnpsgrz2l3lu">
                  Marital Status <span className="text-red-500" data-id="element-3464" data-mp="w5dt2utcvvlf">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-3465" data-mp="w7i0hmqgrcqg">
                  {['Single', 'Married', 'Other'].map(option => <button key={option} onClick={() => handleInputChange('maritalStatus', option)} className={`py-3 px-4 rounded-lg font-medium transition-all ${formData.maritalStatus === option ? 'bg-[#1E2B55] text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3466" data-mp="mfu9fmxtjxpi">
                      {option}
                    </button>)}
                </div>
              </div>
              {/* Date of Birth */}
              <div data-id="element-3467" data-mp="8gcltaw9eqhq">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3468" data-mp="8hnm30qhxg43">
                  Date of Birth <span className="text-red-500" data-id="element-3469" data-mp="1xvajimey9rf4">*</span>
                </label>
                <input type="date" value={formData.dateOfBirth} onChange={e => handleInputChange('dateOfBirth', e.target.value)} max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3470" data-mp="22s9bozf1zoao" />
              </div>
              {/* Working Sector */}
              <div data-id="element-3471" data-mp="1x8izj90xlgv3">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3472" data-mp="1xb55j486bob7">
                  Working Sector <span className="text-red-500" data-id="element-3473" data-mp="22a8doxjgaane">*</span>
                </label>
                <select value={formData.workingSector} onChange={e => handleInputChange('workingSector', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3474" data-mp="lsav121xvvgs">
                  <option value="" data-id="element-3475" data-mp="s92vvevl8gjv">Select sector</option>
                  <option value="Healthcare" data-id="element-3476" data-mp="nb1ecydt8pai">Healthcare</option>
                  <option value="Education" data-id="element-3477" data-mp="nbkjtfm9xl7n">Education</option>
                  <option value="Government" data-id="element-3478" data-mp="3vqcmxjc49y3t">Government</option>
                  <option value="Private" data-id="element-3479" data-mp="3voi368cl0c1b">Private</option>
                  <option value="Student" data-id="element-3480" data-mp="3rqkd73azllwv">Student</option>
                  <option value="Self-employed" data-id="element-3481" data-mp="9b01slo6ntf7">Self-employed</option>
                  <option value="Unemployed" data-id="element-3482" data-mp="9dcsbn80bc3v">Unemployed</option>
                  <option value="Other" data-id="element-3483" data-mp="4jiakpbyp77f">Other</option>
                </select>
              </div>
            </div>}
          {/* Step 1: Location */}
          {currentStep === 1 && <div className="space-y-4" data-id="element-3484" data-mp="3808odqdscojo">
              {/* City */}
              <div data-id="element-3485" data-mp="1byeys50k95y4">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3486" data-mp="1byx37oxdmqh4">
                  City <span className="text-red-500" data-id="element-3487" data-mp="1gwouqhu40aks">*</span>
                </label>
                <select value={formData.city} onChange={e => handleInputChange('city', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3488" data-mp="1lnyi1bs6r1ti">
                  <option value="" data-id="element-3489" data-mp="xxuhjdaani6q">Select city</option>
                  <option value="Riyadh" data-id="element-3490" data-mp="y0gmr69pplls">Riyadh</option>
                  <option value="Jeddah" data-id="element-3491" data-mp="12x7behzl1pit">Jeddah</option>
                  <option value="Mecca" data-id="element-3492" data-mp="12ztjciqdy71k">Mecca</option>
                  <option value="Medina" data-id="element-3493" data-mp="16ztyk5lb6m9o">Medina</option>
                  <option value="Dammam" data-id="element-3494" data-mp="2ecm9xq7fwpg1">Dammam</option>
                  <option value="Khobar" data-id="element-3495" data-mp="2ec2po2ubhoyu">Khobar</option>
                  <option value="Dhahran" data-id="element-3496" data-mp="2idobidotxdl6">Dhahran</option>
                </select>
              </div>
              {/* District */}
              <div data-id="element-3497" data-mp="6pkkdz6ndfrw">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3498" data-mp="6q2lpa862k6o">
                  District <span className="text-red-500" data-id="element-3499" data-mp="2psg3yoadvsc">*</span>
                </label>
                <select value={formData.district} onChange={e => handleInputChange('district', e.target.value)} disabled={!formData.city} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed" data-id="element-3500" data-mp="3u2wove5u10fc">
                  <option value="" data-id="element-3501" data-mp="36691z6li8vw9">
                    {formData.city ? 'Select District' : 'Please select a city first'}
                  </option>
                  {currentDistricts.map(district => <option key={district} value={district} data-id="element-3502" data-mp="14f5rchbm7hbp">
                      {district}
                    </option>)}
                </select>
              </div>
              {/* Preferred Language */}
              <div data-id="element-3503" data-mp="60fmec240syf">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3504" data-mp="3o8ojpkw04mys">
                  Preferred Language <span className="text-red-500" data-id="element-3505" data-mp="3t79bqx13o0c5">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-3506" data-mp="3t9caq6k8d3t0">
                  {['English', 'Arabic'].map(option => <button key={option} onClick={() => handleInputChange('preferredLanguage', option)} className={`py-3 px-4 rounded-lg font-medium transition-all ${formData.preferredLanguage === option ? 'bg-[#1E2B55] text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3507" data-mp="3j91xily73wdu">
                      {option}
                    </button>)}
                </div>
              </div>
            </div>}
          {/* Step 2: Preferences (Optional) */}
          {currentStep === 2 && <div className="space-y-4" data-id="element-3508" data-mp="2bzsa5tmofp8n">
              <p className="text-sm text-gray-500 mb-4" data-id="element-3509" data-mp="2qvrcenn3uv5v">
                Help us personalize your experience (optional)
              </p>
              {/* Children */}
              <div data-id="element-3510" data-mp="2lyhqvj2u73nb">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3511" data-mp="2zvms95rlvec8">
                  Do you have children?
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-3512" data-mp="2uw1qknl1wrvt">
                  {['Yes', 'No'].map(option => <button key={option} onClick={() => handleInputChange('hasChildren', option)} className={`py-3 px-4 rounded-lg font-medium transition-all ${formData.hasChildren === option ? 'bg-[#1E2B55] text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3513" data-mp="39kxz0lr0s6wy">
                      {option}
                    </button>)}
                </div>
              </div>
              {/* Interests */}
              <div data-id="element-3514" data-mp="3qkr0puuux658">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3515" data-mp="3vmgdbexq9rvf">
                  Interests (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-3516" data-mp="48tthwov24gg">
                  {['Beauty', 'Wellness', 'Mother & Baby', 'Sports', 'Nutrition', 'Skincare'].map(interest => <button key={interest} onClick={() => {
                const interests = formData.interests.includes(interest) ? formData.interests.filter(i => i !== interest) : [...formData.interests, interest];
                setFormData({
                  ...formData,
                  interests
                });
              }} className={`py-3 px-4 rounded-lg font-medium transition-all ${formData.interests.includes(interest) ? 'bg-[#1E2B55] text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3517" data-mp="i57f9i0fgs0q">
                      {interest}
                    </button>)}
                </div>
              </div>
            </div>}
          {/* Step 3: Communication (Optional) */}
          {currentStep === 3 && <div className="space-y-4" data-id="element-3518" data-mp="1fzqd1yo3xypf">
              <p className="text-sm text-gray-500 mb-4" data-id="element-3519" data-mp="1g2mehwz5z5im">
                Stay updated with exclusive offers and health tips
              </p>
              {/* Communication Preferences */}
              <div className="space-y-3" data-id="element-3520" data-mp="4jjn7rtorn7q">
                {[{
              key: 'smsConsent',
              label: 'SMS Messages'
            }, {
              key: 'whatsappConsent',
              label: 'WhatsApp'
            }, {
              key: 'emailConsent',
              label: 'Email'
            }, {
              key: 'pushConsent',
              label: 'Push Notifications'
            }].map(option => <div key={option.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-3521" data-mp="2ig2by9yzsy6g">
                    <span className="text-sm font-medium text-gray-700" data-id="element-3522" data-mp="28opm1nwr8i32">
                      {option.label}
                    </span>
                    <button onClick={() => setFormData({
                ...formData,
                [option.key]: !formData[option.key]
              })} className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${formData[option.key] ? 'bg-[#1E2B55]' : 'bg-gray-300'}`} data-id="element-3523" data-mp="2dmhfwngyme9w">
                      <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${formData[option.key] ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-3524" data-mp="14g7a4fvl6oab" />
                    </button>
                  </div>)}
              </div>
              {progress === 100 && <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-3525" data-mp="6truzkqmu2px">
                  <div className="flex items-start" data-id="element-3526" data-mp="kt9mi7vbbu2z">
                    <CheckIcon className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-3527" data-mp="gqd2qdu1drxg" />
                    <div data-id="element-3528" data-mp="1w6lla4c7tmia">
                      <h4 className="font-bold text-green-900 mb-1" data-id="element-3529" data-mp="1w5u07ng8v769">
                        Profile Complete!
                      </h4>
                      <p className="text-sm text-green-700" data-id="element-3530" data-mp="1s7cr4yd9jmm7">
                        You have unlocked full loyalty benefits and can start
                        earning points with every purchase.
                      </p>
                    </div>
                  </div>
                </div>}
            </div>}
        </div>
        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4" data-id="element-3531" data-mp="1lwpwsh2r0k1g">
          <div className="flex gap-3" data-id="element-3532" data-mp="1zw7de95w4qzq">
            {currentStep > 0 && <button onClick={handleBack} className="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center" data-id="element-3533" data-mp="1vw6zqqqm8jtq">
                <ChevronLeftIcon className="w-5 h-5 mr-1" data-id="element-3534" data-mp="24or2o3dxxakd" />
                Back
              </button>}
            <button onClick={handleNext} disabled={!isStepValid() && steps[currentStep].required} className="flex-1 py-3 px-6 bg-[#1E2B55] text-white font-bold rounded-lg hover:bg-[#162041] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center" data-id="element-3535" data-mp="2jj5f1l301lq2">
              {currentStep === steps.length - 1 ? progress === 100 ? 'Start Earning Points' : 'Save Progress' : <>
                  Continue
                  <ChevronRightIcon className="w-5 h-5 ml-1" data-id="element-3536" data-mp="2jaftb1ek8ior" />
                </>}
            </button>
          </div>
        </div>
      </div>
    </>;
}